/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { Box, Container, Stack, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { MANUFACTURING_PROCESS } from "../data/products";
import {
  Hammer,
  ClipboardCheck,
  ArrowRight,
  Truck,
  Settings,
} from "lucide-react";

/**
 * Brand tokens — adjust these to match the values configured
 * in your original Tailwind theme (tailwind.config.js).
 */
const themeColors = {
  obsidian: "#0b0f19",
  safety: "#f97316", // fallback orange/safety color
  ink: "#111827",
  concrete: "#f3f4f6",
  mutedForeground: "#6b7280",
};
const COLORS = {
  brandRed: "#E4392E",
  brandDark: "#161A20",
  brandBlue: "#1E5AA8",
  slate50: "#F8FAFC",
  slate100: "#F1F5F9",
  slate200: "#E2E8F0",
  slate300: "#CBD5E1",
  slate400: "#94A3B8",
  slate500: "#64748B",
  red50: "#FEF2F2",
};

const FONTS = {
  display: "var(--font-display, 'Inter', sans-serif)",
  sans: "var(--font-sans, 'Inter', sans-serif)",
  mono: "var(--font-mono, 'Roboto Mono', monospace)",
};

const MotionBox = motion(Box);

export default function ManufacturingProcess() {
  return (
    <Box
      component="section"
      id="manufacturing"
      sx={{
        py: 12, // py-24 (24 * 4px = 96px = py 12 * 8px spacing unit... using 12 => 96px)
        backgroundColor: "#fff",
        scrollMarginTop: "48px", // scroll-mt-12
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: "1280px", // max-w-7xl
          mx: "auto",
          px: { xs: 2, sm: 3, lg: 4 },
        }}
      >
        {/* Section Title */}
        <Box
          sx={{
            textAlign: "center",
            maxWidth: "768px", // max-w-3xl
            mx: "auto",
            mb: 10, // mb-20
          }}
        >
          <Typography
            sx={{
              fontFamily: "monospace",
              color: themeColors.safety,
              mb: 3,
              textTransform: "uppercase",
            }}
          >
            [ State-Of-The-Art Workflow ]
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
              fontSize: { xs: "1.875rem", sm: "2.25rem" }, // text-3xl sm:text-4xl
              fontWeight: 800, // font-extrabold
              letterSpacing: "-0.025em", // tracking-tight
              color: COLORS.brandDark,
              mt: 2,
              mb: 2,
            }}
          >
            Our Manufacturing &amp; Quality Flow
          </Typography>

          <Box
            sx={{
              width: "64px", // w-16
              height: "4px", // h-1
              backgroundColor: COLORS.brandRed,
              mx: "auto",
              mb: 3,
              borderRadius: "9999px",
            }}
          />

          <Typography
            sx={{
              fontFamily: FONTS.sans,
              color: COLORS.slate500,
              lineHeight: 1.625,
              fontSize: { xs: "0.875rem", sm: "1rem" }, // text-sm sm:text-base
            }}
          >
            From custom core winding calculations to final vacuum baking and
            on-site load tests, we maintain a meticulous workflow to guarantee
            100% equipment reliability.
          </Typography>
        </Box>

        {/* Timeline Layout */}
        <Box
          sx={{
            position: "relative",
            borderLeft: `1px solid ${COLORS.slate200}`,
            ml: { xs: 2, md: 6 }, // ml-4 md:ml-12
            maxWidth: "1024px", // max-w-5xl
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 6, // space-y-12
          }}
        >
          {MANUFACTURING_PROCESS.map((proc, idx) => (
            <MotionBox
              key={proc.step}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.4, delay: idx * 0.1 }}
              sx={{
                position: "relative",
                pl: { xs: 4, md: 6 }, // pl-8 md:pl-12
                "&:hover .timeline-marker": {
                  backgroundColor: COLORS.brandRed,
                },
                "&:hover .timeline-marker-dot": {
                  backgroundColor: "#fff",
                },
                "&:hover .timeline-step-number": {
                  color: COLORS.brandRed,
                },
                "&:hover .timeline-title": {
                  color: COLORS.brandBlue,
                },
                "&:hover .timeline-card": {
                  backgroundColor: "#fff",
                  boxShadow:
                    "0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
                  borderColor: COLORS.slate200,
                },
              }}
            >
              {/* Pulsing indicator marker */}
              <Box
                className="timeline-marker"
                sx={{
                  position: "absolute",
                  left: "-13px",
                  top: "6px",
                  width: "24px",
                  height: "24px",
                  backgroundColor: "#fff",
                  border: `2px solid ${COLORS.brandRed}`,
                  borderRadius: "9999px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background-color 300ms",
                }}
              >
                <Box
                  className="timeline-marker-dot"
                  sx={{
                    width: "6px",
                    height: "6px",
                    backgroundColor: COLORS.brandRed,
                    borderRadius: "9999px",
                    transition: "background-color 300ms",
                  }}
                />
              </Box>

              {/* Box container */}
              <Box
                className="timeline-card"
                sx={{
                  backgroundColor: COLORS.slate50,
                  border: `1px solid ${COLORS.slate100}`,
                  borderRadius: "16px", // rounded-2xl
                  p: 3,
                  transition: "all 300ms",
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(12, 1fr)" },
                  gap: 2,
                }}
              >
                {/* Step indicator */}
                <Box
                  sx={{
                    gridColumn: { md: "span 2" },
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    className="timeline-step-number"
                    sx={{
                      fontFamily: FONTS.display,
                      fontSize: { xs: "1.875rem", md: "2.25rem" }, // text-3xl md:text-4xl
                      fontWeight: 900, // font-black
                      color: COLORS.slate300,
                      transition: "color 300ms",
                    }}
                  >
                    {proc.step}
                  </Typography>
                </Box>

                {/* Text details */}
                <Box sx={{ gridColumn: { md: "span 10" } }}>
                  <Typography
                    component="h3"
                    className="timeline-title"
                    sx={{
                      fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                      fontWeight: 700,
                      fontSize: "1.125rem", // text-lg
                      color: COLORS.brandDark,
                      mb: 1,
                      transition: "color 300ms",
                    }}
                  >
                    {proc.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: FONTS.sans,
                      fontSize: { xs: "0.75rem", sm: "0.875rem" }, // text-xs sm:text-sm
                      color: COLORS.slate500,
                      lineHeight: 1.625,
                    }}
                  >
                    {proc.description}
                  </Typography>
                </Box>
              </Box>
            </MotionBox>
          ))}
        </Box>

        {/* Extra factory standards showcase */}
        <Box
          sx={{
            mt: 10, // mt-20
            maxWidth: "1024px", // max-w-5xl
            mx: "auto",
            backgroundColor: COLORS.brandDark,
            color: "#fff",
            borderRadius: "24px", // rounded-3xl
            p: 4,
            position: "relative",
            overflow: "hidden",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: 3,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "256px", // w-64
              height: "256px", // h-64
              backgroundColor: `${COLORS.brandRed}1A`, // /10 opacity
              filter: "blur(64px)", // blur-3xl
              borderRadius: "9999px",
            }}
          />

          <Box>
            <Typography
              component="span"
              sx={{
                fontFamily: FONTS.mono,
                color: COLORS.brandRed,
                fontWeight: 700,
                fontSize: "0.75rem", // text-xs
                textTransform: "uppercase",
                letterSpacing: "0.15em", // tracking-widest
              }}
            >
              Standard Compliance Guarantee
            </Typography>

            <Typography
              component="h3"
              sx={{
                fontFamily: FONTS.display,
                fontWeight: 700,
                fontSize: { xs: "1.25rem", sm: "1.5rem" }, // text-xl sm:text-2xl
                mt: 0.5,
                mb: 1.5,
              }}
            >
              Certified Testing Laboratories
            </Typography>

            <Typography
              sx={{
                fontFamily: FONTS.sans,
                color: COLORS.slate400,
                fontSize: { xs: "0.75rem", sm: "0.875rem" }, // text-xs sm:text-sm
                maxWidth: "672px", // max-w-2xl
                lineHeight: 1.625,
              }}
            >
              Every system is loaded and checked for hours using high-precision
              power quality analyzers. We test dielectric strength, thermal
              dissipation under full loads, harmonic distortion rates, and
              correction speeds to make sure they are ready for operation.
            </Typography>
          </Box>

          <Stack direction="row" spacing={2} sx={{ flexShrink: 0 }}>
            <Box
              sx={{
                backgroundColor: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
                px: 2,
                py: 1.5,
                borderRadius: "12px",
                textAlign: "center",
              }}
            >
              <Typography
                component="span"
                sx={{
                  display: "block",
                  fontFamily: FONTS.display,
                  fontWeight: 800,
                  fontSize: "1.25rem", // text-xl
                  color: COLORS.brandRed,
                }}
              >
                99.9%
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: "0.625rem", // text-[10px]
                  color: COLORS.slate400,
                  textTransform: "uppercase",
                  fontFamily: FONTS.mono,
                  fontWeight: 700,
                }}
              >
                Pure Copper
              </Typography>
            </Box>

            <Box
              sx={{
                backgroundColor: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
                px: 2,
                py: 1.5,
                borderRadius: "12px",
                textAlign: "center",
              }}
            >
              <Typography
                component="span"
                sx={{
                  display: "block",
                  fontFamily: FONTS.display,
                  fontWeight: 800,
                  fontSize: "1.25rem", // text-xl
                  color: COLORS.brandBlue,
                }}
              >
                IS-2026
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: "0.625rem", // text-[10px]
                  color: COLORS.slate400,
                  textTransform: "uppercase",
                  fontFamily: FONTS.mono,
                  fontWeight: 700,
                }}
              >
                Compliant
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}
